"use client";

import Image from "@/components/ui/OptimizedImage";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { ENV_PREFIX } from "@/lib/query-keys";
import { LandingNav } from "@/components/landing/LandingNav";
import { FooterSection } from "@/components/landing/FooterSection";
import { PriceFilter } from "@/components/landing/PriceFilter";
import { AttendingBadge } from "@/components/landing/AttendingBadge";
import { useMyEventIds } from "@/hooks/useMyEventIds";
import { buildEventPath } from "@/lib/routes";

type PublicEvent = {
  id: string;
  shortId?: string | null;
  title: string;
  description: string | null;
  date: string;
  locationName: string | null;
  locationPlaceId?: string | null;
  locationPlaceName?: string | null;
  locationFormattedAddress?: string | null;
  locationLat?: number | null;
  locationLng?: number | null;
  city: string | null;
  category: string | null;
  imageUrl: string | null;
  status: string;
  hostName: string;
  priceLabel: string | null;
  originalPriceLabel: string | null;
  tags?: string[] | null;
};

const formatDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date TBA";
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "Asia/Kolkata",
  });
};

type CategoryOption = {
  id: string;
  name: string;
};

const DATE_FILTERS = [
  { id: "", label: "All Dates" },
  { id: "tonight", label: "Tonight" },
  { id: "tomorrow", label: "Tomorrow" },
  { id: "this-weekend", label: "This Weekend" },
  { id: "this-week", label: "This Week" },
];

export function EventsClient() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const myEventIds = useMyEventIds();
  const search = searchParams?.get("search") ?? "";
  const city = searchParams?.get("city") ?? "";
  const cityId = searchParams?.get("cityId") ?? "";
  const category = searchParams?.get("category") ?? "";
  const date = searchParams?.get("date") ?? "";
  // URL carries minPrice/maxPrice in CENTS (matches landing/nav); UI works in rupees.
  const minPriceParam = searchParams?.get("minPrice") ?? "";
  const maxPriceParam = searchParams?.get("maxPrice") ?? "";

  const [searchInput, setSearchInput] = useState(search);
  const [minPrice, setMinPrice] = useState(
    minPriceParam ? String(Math.round(Number(minPriceParam) / 100)) : "",
  );
  const [maxPrice, setMaxPrice] = useState(
    maxPriceParam ? String(Math.round(Number(maxPriceParam) / 100)) : "",
  );
  const [priceFilterOpen, setPriceFilterOpen] = useState(false);
  const priceFilterRef = useRef<HTMLDivElement | null>(null);

  // Keep local price inputs synced to URL when navigating externally.
  /* eslint-disable react-hooks/set-state-in-effect -- intentional resync of editable inputs from URL params, not derived state */
  useEffect(() => {
    setMinPrice(
      minPriceParam ? String(Math.round(Number(minPriceParam) / 100)) : "",
    );
    setMaxPrice(
      maxPriceParam ? String(Math.round(Number(maxPriceParam) / 100)) : "",
    );
  }, [minPriceParam, maxPriceParam]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Close the price filter dropdown when clicking outside.
  useEffect(() => {
    if (!priceFilterOpen) return;
    const handler = (e: MouseEvent) => {
      if (!priceFilterRef.current) return;
      if (!priceFilterRef.current.contains(e.target as Node)) {
        setPriceFilterOpen(false);
      }
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [priceFilterOpen]);

  const { data: categories } = useQuery({
    queryKey: [ENV_PREFIX, "categories"],
    queryFn: () => apiFetch<CategoryOption[]>("categories"),
    staleTime: 30 * 60_000,
  });

  const categoryOptions = useMemo(
    () => [{ id: "", name: "All" }, ...(categories ?? [])],
    [categories],
  );

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      const params = new URLSearchParams(searchParams?.toString() ?? "");
      for (const [key, value] of Object.entries(updates)) {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      }
      const qs = params.toString();
      router.push(qs ? `/events?${qs}` : "/events", { scroll: false });
    },
    [router, searchParams],
  );

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      updateParams({ search: searchInput.trim() });
    },
    [searchInput, updateParams],
  );

  const applyPriceFilter = useCallback(() => {
    // Store as cents in the URL to match the rest of the app (landing nav).
    const next: Record<string, string> = {};
    next.minPrice = minPrice ? String(Number(minPrice) * 100) : "";
    next.maxPrice = maxPrice ? String(Number(maxPrice) * 100) : "";
    updateParams(next);
    setPriceFilterOpen(false);
  }, [minPrice, maxPrice, updateParams]);

  const clearPriceFilter = useCallback(() => {
    setMinPrice("");
    setMaxPrice("");
    updateParams({ minPrice: "", maxPrice: "" });
    setPriceFilterOpen(false);
  }, [updateParams]);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      ENV_PREFIX,
      "events",
      { search, city, cityId, category, date, minPriceParam, maxPriceParam },
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (search) {
        params.set("search", search);
      }
      if (cityId) {
        params.set("cityId", cityId);
      } else if (city) {
        params.set("city", city);
      }
      if (category) {
        params.set("category", category);
      }
      if (date) {
        params.set("date", date);
      }
      // Pass price bounds through directly (already in cents).
      if (minPriceParam) {
        params.set("minPrice", minPriceParam);
      }
      if (maxPriceParam) {
        params.set("maxPrice", maxPriceParam);
      }
      params.set("upcoming", "true");
      const query = params.toString();
      const endpoint = `events?${query}`;
      return apiFetch<PublicEvent[]>(endpoint);
    },
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
  const events = data ?? [];
  const errorMessage =
    error instanceof Error ? error.message : "Unable to load events.";

  const sortedEvents = useMemo(
    () =>
      [...events].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      ),
    [events],
  );

  const displayCity = city || "All cities";

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-black text-[var(--hopp-text)]">
      {/* U-shaped gradient fixed to viewport */}
      <div className="pointer-events-none fixed inset-0 z-[1]" style={{ contain: "strict" }}>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_6%_at_50%_100%,rgba(23,21,104,0.6),transparent_100%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_10%_35%_at_0%_100%,rgba(23,21,104,0.6),transparent_100%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_10%_35%_at_100%_100%,rgba(23,21,104,0.6),transparent_100%)]" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }}
        />
      </div>
      <LandingNav />
      <main className="relative z-10 isolate">
        <div className="relative z-10 px-4 pb-24 pt-12 md:pb-16 md:pt-14 lg:px-12">
          <div className="mx-auto w-full max-w-6xl">
            {/* Header */}
            <div className="mb-2 flex items-center justify-between md:mb-4">
              <h2 className="text-sm font-semibold text-[var(--hopp-text)]">
                Events
              </h2>
              <span className="text-[10px] uppercase tracking-[0.3em] text-[var(--hopp-muted)]">
                in {displayCity}
              </span>
            </div>

            {/* Filter bar */}
            <div className="space-y-4">
              {/* Search input */}
              <form onSubmit={handleSearchSubmit} className="relative">
                <svg
                  className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search events..."
                  className="w-full rounded-xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] py-2.5 pl-10 pr-4 text-sm text-white placeholder:text-white/40 outline-none transition-colors focus:border-[var(--hopp-sapphire)]"
                />
              </form>

              {/* Category pills */}
              <div className="scrollbar-hide -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
                {categoryOptions.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => updateParams({ category: cat.id })}
                    className={`flex-shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium transition-all duration-150 ${
                      category === cat.id
                        ? "border border-white/30 bg-white/15 text-[#EDEDED]"
                        : "border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] text-white/60 hover:border-white/20 hover:text-white hover:bg-white/10"
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              {/* Date quick-filters + Price filter */}
              <div className="flex flex-wrap items-center gap-2 pb-1">
                <div className="scrollbar-hide -mx-1 flex flex-1 gap-2 overflow-x-auto px-1">
                  {DATE_FILTERS.map((filter) => (
                    <button
                      key={filter.id}
                      type="button"
                      onClick={() => updateParams({ date: filter.id })}
                      className={`flex-shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium transition-all duration-150 ${
                        date === filter.id
                          ? "border border-white/30 bg-white/15 text-[#EDEDED]"
                          : "border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] text-white/60 hover:border-white/20 hover:text-white hover:bg-white/10"
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
                <PriceFilter
                  minPrice={minPrice}
                  maxPrice={maxPrice}
                  onMinPriceChange={setMinPrice}
                  onMaxPriceChange={setMaxPrice}
                  onApply={applyPriceFilter}
                  onClear={clearPriceFilter}
                  isOpen={priceFilterOpen}
                  onToggle={() => setPriceFilterOpen((v) => !v)}
                  containerRef={priceFilterRef}
                  dropdownAlign="right"
                />
              </div>

              {/* Active filters summary */}
              {(search || category || date || minPriceParam || maxPriceParam) && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">
                    Filters:
                  </span>
                  {search && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] px-2.5 py-1 text-[11px] text-white/70">
                      &quot;{search}&quot;
                      <button
                        type="button"
                        onClick={() => {
                          setSearchInput("");
                          updateParams({ search: "" });
                        }}
                        className="text-white/40 hover:text-white"
                      >
                        x
                      </button>
                    </span>
                  )}
                  {category && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] px-2.5 py-1 text-[11px] text-white/70">
                      {categoryOptions.find((c) => c.id === category)?.name ?? category}
                      <button
                        type="button"
                        onClick={() => updateParams({ category: "" })}
                        className="text-white/40 hover:text-white"
                      >
                        x
                      </button>
                    </span>
                  )}
                  {date && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] px-2.5 py-1 text-[11px] text-white/70">
                      {DATE_FILTERS.find((f) => f.id === date)?.label ?? date}
                      <button
                        type="button"
                        onClick={() => updateParams({ date: "" })}
                        className="text-white/40 hover:text-white"
                      >
                        x
                      </button>
                    </span>
                  )}
                  {(minPriceParam || maxPriceParam) && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] px-2.5 py-1 text-[11px] text-white/70">
                      {minPriceParam
                        ? `\u20B9${Math.round(Number(minPriceParam) / 100)}`
                        : "\u20B90"}
                      {maxPriceParam
                        ? `\u2013\u20B9${Math.round(Number(maxPriceParam) / 100)}`
                        : "+"}
                      <button
                        type="button"
                        onClick={clearPriceFilter}
                        className="text-white/40 hover:text-white"
                      >
                        x
                      </button>
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setSearchInput("");
                      setMinPrice("");
                      setMaxPrice("");
                      router.push("/events", { scroll: false });
                    }}
                    className="text-[11px] text-white/50 hover:text-white underline underline-offset-2"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>

            {isLoading ? (
              <div className="mt-8 grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 md:grid-cols-4 lg:grid-cols-5">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div
                    key={i}
                    className="w-full overflow-hidden rounded-xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)]"
                  >
                    <div className="aspect-[4/3] animate-pulse bg-white/5" />
                    <div className="p-3">
                      <div className="min-h-[2.5em] animate-pulse rounded bg-white/5" />
                      <div className="mt-1 h-3 w-16 animate-pulse rounded bg-white/5" />
                      <div className="mt-1 h-3 w-20 animate-pulse rounded bg-white/5" />
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="mt-8 rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-6 text-sm text-red-200">
                {errorMessage}
              </div>
            ) : sortedEvents.length === 0 ? (
              <div className="mt-8 rounded-2xl bg-[var(--hopp-panel)] px-6 py-8 text-center">
                <p className="text-sm font-semibold text-[var(--hopp-text)]">
                  No events found.
                </p>
                <p className="mt-2 text-xs text-[var(--hopp-muted)]">
                  Try adjusting your filters or check back soon.
                </p>
              </div>
            ) : (
              <div className="mt-8 grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 md:grid-cols-4 lg:grid-cols-5">
                {sortedEvents.map((event, eventIndex) => {
                  const tag = event.tags?.[0] ?? event.category;
                  const priceBadge = event.priceLabel
                    ? event.priceLabel.toLowerCase().includes("free")
                      ? "Free"
                      : event.priceLabel
                    : null;

                  return (
                    <Link
                      key={event.id}
                      href={buildEventPath(event.shortId ?? event.id, event.title)}
                      className="flex-shrink-0"
                    >
                      <div className="group w-full overflow-hidden rounded-xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] transition-transform duration-150 ease-out hover:-translate-y-0.5">
                        {/* Image */}
                        <div className="relative aspect-[4/3] overflow-hidden">
                          {event.imageUrl ? (
                            <Image
                              src={event.imageUrl}
                              alt={event.title}
                              fill
                              priority={eventIndex < 2}
                              sizes="(max-width: 640px) 46vw, (max-width: 768px) 30vw, (max-width: 1024px) 23vw, 215px"
                              className="object-cover transition-transform duration-150 group-hover:scale-105"
                            />
                          ) : (
                            <div className="h-full w-full bg-gradient-to-br from-[rgba(58,92,255,0.25)] via-[rgba(16,16,24,0.9)] to-[rgba(0,0,0,0.9)]" />
                          )}
                          {tag ? (
                            <span className="absolute left-2 top-2 rounded-full bg-[rgba(8,12,24,0.85)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/90 shadow-[0_8px_18px_rgba(0,0,0,0.35)]">
                              {tag}
                            </span>
                          ) : null}
                          {myEventIds.has(event.id) ? <AttendingBadge /> : null}
                          {priceBadge ? (
                            <span className="absolute bottom-2 right-2 rounded-md bg-[rgba(8,12,24,0.82)] px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-white/85 shadow-[0_8px_18px_rgba(0,0,0,0.35)]">
                              {priceBadge === "Free" ? (
                                priceBadge
                              ) : (
                                <span className="flex flex-col items-end">
                                  <span className="text-[8px] font-medium tracking-[0.24em] text-white/70">
                                    Starting from
                                  </span>
                                  <span className="translate-x-1 text-[10px] font-semibold tracking-[0.2em] text-white">
                                    {event.priceLabel}
                                  </span>
                                  <span className="text-[7px] font-normal tracking-[0.18em] text-white/50">
                                    + platform fee
                                  </span>
                                </span>
                              )}
                            </span>
                          ) : null}
                        </div>

                        {/* Content */}
                        <div className="p-3">
                          <h3 className="line-clamp-2 min-h-[2.5em] text-xs font-semibold leading-snug text-[var(--hopp-text)]">
                            {event.title}
                          </h3>
                          <p className="mt-1 text-[10px] text-white/60">
                            {formatDate(event.date)}
                          </p>
                          <p className="mt-1 truncate text-[10px] text-white/60">
                            by {event.hostName}
                          </p>
                          {event.locationName && (
                            <p className="mt-1 truncate text-[10px] text-white/60">
                              {event.locationName}
                            </p>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
      <FooterSection />
    </div>
  );
}
